'use client';

import { useMemo, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { ArrowDown, ArrowUp, Image as ImageIcon, Search, Volume2, X } from 'lucide-react';

import {
  estCeny,
  estFiltry,
  otobratModeli,
  otsortirovatModeli,
  pokazatCenu,
  pokazatKontekst,
  postavshchiki,
  PUSTOJ_ZAPROS,
  stranicaModelej,
  type PoleSortirovki,
  type StrokaModeli,
  type ZaprosModelej,
} from '@/lib/modeli-ceny';

/**
 * Таблица цен на модели.
 *
 * Весь каталог приходит с сервера одним куском и дальше живёт в
 * браузере: `GET /llm/models` не принимает ни поиска, ни сортировки, ни
 * смещения, поэтому ходить за каждой страницей в сеть было бы и
 * медленнее, и бессмысленно — данные уже все здесь. Отсюда и отличие от
 * таблицы прокси по соседству, где наоборот всё считает API.
 *
 * Состояние намеренно не уходит в адресную строку, в отличие от прокси:
 * таблица стоит внизу страницы стоимости, а не является самой
 * страницей. Менять адрес витрины из-за выбранного фильтра значит
 * ломать кнопку «назад» тому, кто просто листал тарифы.
 */

/** Сколько поставщиков показывать кнопками. Их около сорока, и полный
 *  список занял бы больше места, чем сама таблица. */
const POSTAVSHCHIKOV_VIDNO = 8;

export function ModeliTablica({ modeli }: { modeli: StrokaModeli[] }) {
  const t = useTranslations('modeli');
  const locale = useLocale();

  const [zapros, setZapros] = useState<ZaprosModelej>(PUSTOJ_ZAPROS);

  const spisokPostavshchikov = useMemo(() => postavshchiki(modeli), [modeli]);
  const ceny = useMemo(() => estCeny(modeli), [modeli]);

  const stranica = useMemo(() => {
    const otobrannye = otobratModeli(modeli, zapros);
    const otsortirovannye = otsortirovatModeli(otobrannye, zapros.sort, zapros.ubyvanie);
    return stranicaModelej(otsortirovannye, zapros.stranica);
  }, [modeli, zapros]);

  // Любая смена фильтра возвращает на первую страницу: иначе человек,
  // стоявший на пятой, после ввода поиска видел бы пустоту.
  const menyat = (chast: Partial<ZaprosModelej>) => {
    setZapros((z) => ({ ...z, ...chast, stranica: 1 }));
  };

  const sortirovat = (pole: PoleSortirovki) => {
    setZapros((z) =>
      // Повторное нажатие по той же колонке переворачивает порядок: так
      // ведут себя таблицы везде, и объяснять это не приходится.
      z.sort === pole
        ? { ...z, ubyvanie: !z.ubyvanie, stranica: 1 }
        : { ...z, sort: pole, ubyvanie: false, stranica: 1 },
    );
  };

  const perekluchitPostavshchika = (imya: string) => {
    setZapros((z) => ({
      ...z,
      postavshchiki: z.postavshchiki.includes(imya)
        ? z.postavshchiki.filter((p) => p !== imya)
        : [...z.postavshchiki, imya],
      stranica: 1,
    }));
  };

  if (modeli.length === 0) {
    // Каталог не пришёл. Молча показывать пустую таблицу нельзя: это
    // выглядит как «моделей нет», хотя их почти три сотни.
    return (
      <p className="text-center text-sm text-[var(--color-text-muted)]">{t('nedostupno')}</p>
    );
  }

  const zagolovok = (pole: PoleSortirovki, podpis: string, spravaKolonka = false) => (
    <th className={`py-3 px-3 font-medium ${spravaKolonka ? 'text-right' : 'text-left'}`}>
      <button
        type="button"
        onClick={() => {
          sortirovat(pole);
        }}
        className={`inline-flex items-center gap-1 hover:text-[var(--color-accent-400)] transition-colors ${
          zapros.sort === pole ? 'text-[var(--color-accent-400)]' : ''
        }`}
      >
        {podpis}
        {zapros.sort === pole &&
          (zapros.ubyvanie ? (
            <ArrowDown className="w-3 h-3" />
          ) : (
            <ArrowUp className="w-3 h-3" />
          ))}
      </button>
    </th>
  );

  return (
    <section className="mt-20">
      <h2 className="text-2xl font-bold mb-2 text-center">{t('title')}</h2>
      <p className="text-sm text-[var(--color-text-muted)] text-center mb-8 max-w-2xl mx-auto">
        {ceny ? t('subtitle') : t('bez_cen')}
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)]" />
          <input
            type="search"
            value={zapros.poisk}
            onChange={(e) => {
              // Без задержки: список уже в памяти, запроса в сеть нет, и
              // ждать паузы в наборе не за чем.
              menyat({ poisk: e.target.value });
            }}
            placeholder={t('poisk')}
            aria-label={t('poisk')}
            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-[var(--color-bg-card)] border border-[var(--color-border)] text-sm focus:border-[var(--color-accent-500)] outline-none"
          />
        </div>

        <button
          type="button"
          onClick={() => {
            menyat({ tolkoBesplatnye: !zapros.tolkoBesplatnye });
          }}
          aria-pressed={zapros.tolkoBesplatnye}
          className={`px-3 py-2.5 rounded-xl text-sm border transition-colors ${
            zapros.tolkoBesplatnye
              ? 'border-[var(--color-accent-500)] text-[var(--color-accent-400)]'
              : 'border-[var(--color-border)] text-[var(--color-text-muted)]'
          }`}
        >
          {t('besplatnye')}
        </button>

        <button
          type="button"
          onClick={() => {
            menyat({ tolkoKartinki: !zapros.tolkoKartinki });
          }}
          aria-pressed={zapros.tolkoKartinki}
          className={`px-3 py-2.5 rounded-xl text-sm border transition-colors ${
            zapros.tolkoKartinki
              ? 'border-[var(--color-accent-500)] text-[var(--color-accent-400)]'
              : 'border-[var(--color-border)] text-[var(--color-text-muted)]'
          }`}
        >
          {t('s_kartinkami')}
        </button>

        {estFiltry(zapros) && (
          <button
            type="button"
            onClick={() => {
              setZapros(PUSTOJ_ZAPROS);
            }}
            className="inline-flex items-center gap-1 px-3 py-2.5 rounded-xl text-sm border border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-accent-500)] transition-colors"
          >
            <X className="w-3.5 h-3.5" />
            {t('sbros')}
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {spisokPostavshchikov.slice(0, POSTAVSHCHIKOV_VIDNO).map((p) => (
          <button
            key={p.imya}
            type="button"
            onClick={() => {
              perekluchitPostavshchika(p.imya);
            }}
            aria-pressed={zapros.postavshchiki.includes(p.imya)}
            className={`px-2.5 py-1 rounded-lg text-xs border transition-colors ${
              zapros.postavshchiki.includes(p.imya)
                ? 'border-[var(--color-accent-500)] text-[var(--color-accent-400)]'
                : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-accent-500)]/50'
            }`}
          >
            {p.imya} <span className="opacity-60">{p.skolko}</span>
          </button>
        ))}
      </div>

      {/*
        Горизонтальная прокрутка вместо сжатия: на телефоне пять колонок
        иначе схлопываются в нечитаемую кашу, а имя модели длинное и
        переносить его негде.
      */}
      <div className="overflow-x-auto rounded-2xl border border-[var(--color-border)]">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
            <tr>
              {zagolovok('id', t('model'))}
              {ceny && zagolovok('vhod', t('vhod'), true)}
              {ceny && zagolovok('vyhod', t('vyhod'), true)}
              {zagolovok('kontekst', t('kontekst'), true)}
              <th className="py-3 px-3 font-medium text-right">{t('umeet')}</th>
            </tr>
          </thead>
          <tbody>
            {stranica.strok.map((m) => (
              <tr
                key={m.id}
                className="border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-bg-card)]"
              >
                <td className="py-2.5 px-3">
                  <span className="font-mono text-xs">{m.id}</span>
                  {m.besplatnaya && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-[var(--color-accent-500)]/15 text-[var(--color-accent-400)]">
                      {t('besplatno')}
                    </span>
                  )}
                </td>
                {ceny && (
                  <td className="py-2.5 px-3 text-right whitespace-nowrap">
                    {pokazatCenu(m.vhod, m.valyuta, locale)}
                  </td>
                )}
                {ceny && (
                  <td className="py-2.5 px-3 text-right whitespace-nowrap">
                    {pokazatCenu(m.vyhod, m.valyuta, locale)}
                  </td>
                )}
                <td className="py-2.5 px-3 text-right text-[var(--color-text-muted)]">
                  {pokazatKontekst(m.kontekst)}
                </td>
                <td className="py-2.5 px-3">
                  <span className="flex items-center justify-end gap-2 text-[var(--color-text-muted)]">
                    {m.kartinki && <ImageIcon className="w-3.5 h-3.5" aria-label={t('kartinki')} />}
                    {m.zvuk && <Volume2 className="w-3.5 h-3.5" aria-label={t('zvuk')} />}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {stranica.vsego === 0 && (
        <p className="text-center text-sm text-[var(--color-text-muted)] py-8">{t('pusto')}</p>
      )}

      {stranica.stranic > 1 && (
        <div className="flex items-center justify-center gap-4 mt-5 text-sm">
          <button
            type="button"
            disabled={stranica.stranica <= 1}
            onClick={() => {
              setZapros((z) => ({ ...z, stranica: z.stranica - 1 }));
            }}
            className="px-3 py-1.5 rounded-lg border border-[var(--color-border)] disabled:opacity-40 hover:border-[var(--color-accent-500)] transition-colors"
          >
            {t('nazad')}
          </button>
          <span className="text-[var(--color-text-muted)]">
            {t('stranica', { n: stranica.stranica, vsego: stranica.stranic })}
          </span>
          <button
            type="button"
            disabled={stranica.stranica >= stranica.stranic}
            onClick={() => {
              setZapros((z) => ({ ...z, stranica: z.stranica + 1 }));
            }}
            className="px-3 py-1.5 rounded-lg border border-[var(--color-border)] disabled:opacity-40 hover:border-[var(--color-accent-500)] transition-colors"
          >
            {t('vperyod')}
          </button>
        </div>
      )}

      <p className="text-center text-xs text-[var(--color-text-muted)] mt-4">
        {t('vsego', { n: stranica.vsego })}
      </p>
    </section>
  );
}
